import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Edit, CheckCircle, X, Camera, Lock } from 'lucide-react';

function Profile() {
    const { currentUser, logout, updateProfile, updateAvatar } = useAuth();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    
    
    const [formData, setFormData] = useState({
        firstName: currentUser?.first_name || '',
        lastName: currentUser?.last_name || '',
        email: currentUser?.email || '',
        username: currentUser?.username || '',
        phone: currentUser?.phone || '',
        city: currentUser?.city || '',
        state: currentUser?.state || ''
    });
    
    // Update form data when currentUser changes
    useEffect(() => {
        if (currentUser) {
            setFormData({
                firstName: currentUser.firstName || currentUser.first_name || '',
                lastName: currentUser.lastName || currentUser.last_name || '',
                email: currentUser.email || '',
                username: currentUser.username || '',
                phone: currentUser.phone || '',
                city: currentUser.city || '',
                state: currentUser.state || ''
            });
        }
    }, [currentUser]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleFileChange = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        
        const file = e.target.files[0];
        if (!file.type.match('image.*')) {
            setError('Please select an image file');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            
            const result = await updateAvatar(formData);
            
            if (result.success) {
                setSuccess('Profile photo updated successfully');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.error || 'Failed to update profile photo');
            }
        } catch (err) {
            setError('An error occurred while updating profile photo');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        try {
            const result = await updateProfile(formData);
            
            if (result.success) {
                setIsEditing(false);
                setSuccess('Profile updated successfully');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.error || 'Failed to update profile');
            }
        } catch (err) {
            setError('An error occurred while updating profile');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    

    return (
        <div className="h-full overflow-y-auto p-6 max-w-screen-2xl mx-auto">
            <div className="max-w-3xl mx-auto space-y-8">
            <div className="space-y-2">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-unna font-bold">Your Profile</h1>
                    {!isEditing && (
                        <button 
                            onClick={() => setIsEditing(true)}
                            className="px-4 py-2 bg-blue-500 font-rasa text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                        >
                            <Edit size={16} />
                            <span>Edit Profile</span>
                        </button>
                    )}
                </div>
                <p className="text-gray-500 font-rasa dark:text-gray-400">
                    {isEditing ? "Edit your personal information" : "Your account information"}
                </p>
            </div>
            
            {/* Success/Error message */}
            {success && (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 flex items-center">
                    <CheckCircle size={18} className="mr-2" />
                    {success}
                </div>
            )}
            
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 flex items-center">
                    <X size={18} className="mr-2" />
                    {error}
                </div>
            )}
            
            {/* Profile card */}
            <div className="bg-white font-unna dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4 mb-6">
                    <div className="relative">
                        <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                            {currentUser?.avatarUrl ? (
                                <img 
                                    src={currentUser.avatarUrl} 
                                    alt={`${currentUser.first_name} ${currentUser.last_name}`} 
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                (currentUser?.firstName || currentUser?.first_name)?.charAt(0) || 'U'
                            )}
                        </div>
                        
                        {isEditing && (
                            <>
                                <button 
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute bottom-0 right-0 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors"
                                    disabled={loading}
                                >
                                    <Camera size={14} />
                                </button>
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    accept="image/*" 
                                    className="hidden" 
                                    onChange={handleFileChange}
                                    disabled={loading}
                                />
                            </>
                        )}
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold">{currentUser?.firstName || currentUser?.first_name} {currentUser?.lastName || currentUser?.last_name}</h2>
                        <p className="text-gray-500 dark:text-gray-400">{currentUser?.email}</p>
                    </div>
                </div>

                {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border text-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium mb-1">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border text-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>
                        
                        {/* <div>
                            <label className="block text-sm font-medium mb-1">Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border text-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div> */}
                        
                        <div>
                            <label className="block text-sm font-medium mb-1">Username</label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border text-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium mb-1">Phone</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border text-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">City</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border text-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium mb-1">State</label>
                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-lg text-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        
                        <div className="border-t dark:border-gray-700 pt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/dashboard/password-change')}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <Lock size={16} />
                                <span>{currentUser?.has_password === false ? 'Set Password' : 'Change Password'}</span>
                            </button>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            <button 
                                type="button" 
                                onClick={() => setIsEditing(false)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="space-y-4 mt-6">
                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <h3 className="font-medium mb-3">Profile Information</h3>
                            
                            <div className="grid grid-cols-1 gap-4">
                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Username:</span>
                                    <span className="font-medium">{currentUser?.username}</span>
                                </div>
                                
                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Email:</span>
                                    <span className="font-medium">{currentUser?.email}</span>
                                </div>
                                
                                <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Name:</span>
                                    <span className="font-medium">{currentUser?.firstName || currentUser?.first_name} {currentUser?.lastName || currentUser?.last_name}</span>
                                </div>

                                {currentUser?.phone && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                                        <span className="font-medium">{currentUser?.phone}</span>
                                    </div>
                                )}

                                {currentUser?.city && currentUser?.state && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Location:</span>
                                        <span className="font-medium">{currentUser?.city}, {currentUser?.state}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        
                        
                        <div className="flex justify-center mt-6">
                            <button 
                                onClick={logout}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                                <LogOut size={18} />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </div>
    );
}

export default Profile;